import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NzFlexDirective } from 'ng-zorro-antd/flex';
import { NzTypographyComponent } from 'ng-zorro-antd/typography';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { UserStore } from '../../app-state/user.store';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AuthTeardownService } from '../../services/auth/auth-teardown.service';
import { AuthService } from '../../services/auth/auth.service';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { KeycloakService } from '../../services/keycloak/keycloak.service';
import { SubscriptionStore } from '../../app-state/subscription.store';

@Component({
  selector: 'app-navbar',
  imports: [
    NzFlexDirective,
    NzTypographyComponent,
    NzButtonComponent,
    NzAvatarModule,
    NzDropDownModule,
    NzIconDirective,
    NzModalModule,
    NzMenuModule,
    RouterLink,
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  standalone: true,
})
export class NavbarComponent {
  userStore = inject(UserStore);
  private readonly authTeardown = inject(AuthTeardownService);
  user = this.userStore.user;
  organizationUser = this.userStore.organizationUser;
  organization = this.userStore.organization;
  token = this.userStore.token;
  authService = inject(AuthService);
  keycloakService = inject(KeycloakService);
  subscriptionStore = inject(SubscriptionStore);
  modal = inject(NzModalService);
  message = inject(NzMessageService);
  router = inject(Router);
  isLoggedIn = computed(
    () =>
      !!(
        this.userStore.user() &&
        this.userStore.organizationUser() &&
        this.userStore.organization() &&
        this.userStore.token()
      ),
  );

  isDropdownOpen = signal<boolean>(false);

  /**
   * Rendered only from the server-derived Complimentary Access end timestamp
   * held by SubscriptionStore. There is intentionally no timer or Navbar API
   * request; it changes on the next existing entitlement refresh/navigation.
   */
  complimentaryAccessLabel = computed(() => {
    if (!this.subscriptionStore.hasActiveSubscription()) return null;
    const endTimestamp = this.subscriptionStore.complimentaryAccessEnd();
    if (endTimestamp === null) return null;

    const end = new Date(endTimestamp * 1000);
    if (!Number.isFinite(end.getTime())) return null;

    const now = new Date();
    const todayStart = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();
    const endDayStart = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
    ).getTime();
    const daysRemaining = Math.round(
      (endDayStart - todayStart) / (24 * 60 * 60 * 1000),
    );

    if (daysRemaining < 0) return null;
    if (daysRemaining === 0) return 'Ends today';
    return `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} remaining`;
  });

  /** True when on the public patient questionnaire page (token link). */
  isPatientQuestionnairePage(): boolean {
    return this.router.url.includes('patient-questionnaire');
  }

  onDropdownVisibleChange(visible: boolean): void {
    this.isDropdownOpen.set(visible);
  }

  routeToLogin(): void {
    this.router.navigate(['/auth/login']).then();
  }

  logout(): void {
    this.authService.logout().subscribe();
    this.keycloakService.resetLoginFlow();
    this.authTeardown.clearAuthentication();
    localStorage.clear();
    this.routeToLogin();
  }

  redirectToHomePage(): void {
    // Do not navigate to HealthNote from the patient questionnaire page; keep patient flow separate
    if (this.isPatientQuestionnairePage()) {
      return;
    }
    this.router.navigate(['/home']).then();
  }
}
